import { Text } from "@shopify/polaris"
import classnames from "classnames"
import { useCallback, useEffect, useRef, useState } from "react"

import style from "./index.module.scss"

type radioOption = {
  value: string;
  label: string;
};

type ISwitchTabProps = {
  className?: string,
  radioOptions: Array<radioOption>;
  value: string;
  handleRadio: (value: string) => void;
  radioStyle?: React.CSSProperties;
  radioActiveBgBoxStyle?: React.CSSProperties;
};

const SwitchTab = (props: ISwitchTabProps) => {
  const {
    className,
    radioOptions,
    value,
    handleRadio,
    radioStyle,
    radioActiveBgBoxStyle,
  } = props

  // 保存每个Tab元素的 ref
  const itemRefs = useRef<HTMLDivElement[]>([])

  const [radioWidth, setRadioWidth] = useState(0)
  const [bgOffset, setBgOffset] = useState(0)

  // 计算当前选中 Tab 的宽度与背景块偏移（抽成函数便于在 resize / 视口切换时复用）
  const recalculateRadioWidth = useCallback(() => {
    if (!radioOptions.length) return

    const index = radioOptions.findIndex((item) => item.value === value)
    let target: HTMLDivElement | null = null

    radioOptions.forEach((item, i) => {
      if (item.value === value) target = itemRefs.current[i]
    })

    if (target) {
      setRadioWidth((target as HTMLDivElement).clientWidth)
    }

    if (index !== -1) {
      let offset = 0

      for (let i = 0; i < index; i++) {
        offset += itemRefs.current[i]?.clientWidth ?? 0
        if (offset !== 0) offset += 6
      }
      setBgOffset(offset)
    } else {
      setBgOffset(0)
    }
  }, [radioOptions, value])

  // 初始化 ref 数组并在 radioOptions / value 变化时计算宽度与偏移
  useEffect(() => {
    itemRefs.current = new Array(radioOptions.length).fill(null)
      .map((_, index) => itemRefs.current[index] || null)
    recalculateRadioWidth()
  }, [radioOptions, recalculateRadioWidth])

  // 视口变化时重新计算（解决 PC hidden / 移动端 block 等双组件场景下宽度为 0 的问题）
  useEffect(() => {
    const handleResize = () => recalculateRadioWidth()

    window.addEventListener("resize", handleResize)

    const mq = window.matchMedia("(min-width: 768px)")
    const handleMediaChange = () => recalculateRadioWidth()

    mq.addEventListener("change", handleMediaChange)

    return () => {
      window.removeEventListener("resize", handleResize)
      mq.removeEventListener("change", handleMediaChange)
    }
  }, [recalculateRadioWidth])

  return (
    <div className={classnames(style.radiosBoxBorderWrapper, className)}>
      <div className={style.radiosBox}>
        <div className={style.radioActiveBgBox} style={radioActiveBgBoxStyle}>
          {!!radioWidth && <div
            className={style.radioActiveBg}
            // 动画样式绑定
            style={{
              width: `${radioWidth}px`,
              transform: `translateX(${bgOffset}px)`,
            }}
          />
          }
        </div>

        {radioOptions.map((options, index) => {
          return (
            <div
              key={options.value}
              className={style.radioBox}
              ref={(el: HTMLDivElement) => (itemRefs.current[index] = el)}
              onClick={() => handleRadio(options.value)}
            >
              <div
                className={classnames({
                  [style.radio]: true,
                  [style.radioActive]: value === options.value,
                })}
                style={radioStyle}
              >
                <Text as={"span"} variant={"bodyMd"} fontWeight={"medium"}>
                  {options.label}
                </Text>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default SwitchTab
